import {BrowserRouter, Navigate, Route, Routes, useLocation} from "react-router-dom";
import {useEffect, useState} from "react";
import application from "./core/application";
import {LoginPage} from "./authentication/login.page";
import {SellPage} from "./sell/pages/sell.page";
import {ProductsPage} from "./product/pages/products.page";
import {ProductFormPage} from "./product/pages/product-form.page";
import {ProductCategoriesPage} from "./product/pages/product-categories.page";
import {ProductsupplierPage} from "./product/pages/product-supplier.page";
import {UsersPage} from "./users/pages/users.page";
import {NotPermissionPage} from "./users/pages/not-permission.page";
import {DashboardPage} from "./dashboard/pages/dashboard.page";
import {SettingsPage} from "./settings/pages/settings.page";
import {MyPlanPage} from "./settings/pages/my-plan.page";
import {ProductQrcodePage} from "./product/pages/product-qrcode.page";
import {ThemeProvider, CssBaseline} from "@mui/material";
import {appTheme} from "./theme/app-theme";
import {LoadingScreen} from "./app/loading-screen";
import {SalesAnalyticsPage} from "./sell/pages/sales-analytics.page";
import {SaleDetailPage} from "./sell/pages/sale-detail.page";
import {CommissionsPage} from "./sell/pages/commissions.page";
import {InventoryPage} from "./product/pages/inventory.page";
import {BulkProductsPage} from "./product/pages/bulk-products.page";
import {CashRegisterProvider} from "./cash-register/cash-register-context";
import {CashRegisterGate} from "./cash-register/cash-register-gate";
import {CashRegisterPage} from "./cash-register/pages/cash-register.page";
import {CashRegisterClosePage} from "./cash-register/pages/cash-register-close.page";
import {CashRegisterReportsPage} from "./cash-register/pages/cash-register-reports.page";
import {CashRegisterSessionDetailPage} from "./cash-register/pages/cash-register-session-detail.page";
import {PayablesListPage} from "./payables/pages/payables-list.page";
import {PayableFormPage} from "./payables/pages/payable-form.page";
import {MarketingHomePage} from "./marketing/pages/marketing-home.page";
import {MarketingPlansPage} from "./marketing/pages/marketing-plans.page";
import {MarketingSubscribePage} from "./marketing/pages/marketing-subscribe.page";
import {MarketingCheckoutPage} from "./marketing/pages/marketing-checkout.page";
import {AiCreditsRequiredDialog, AiWelcomeBonusDialog} from "./ai/ai-credit-guard";
import {CustomersListPage} from "./customer/pages/customers-list.page";
import {CustomerDetailPage} from "./customer/pages/customer-detail.page";
import {CreditReportPage} from "./customer/pages/credit-report.page";
import {KnowledgeBasePage} from "./support/pages/knowledge-base.page";
import {SupportContactPage} from "./support/pages/support-contact.page";
import {OnboardingTour} from "./onboarding/onboarding-tour";
import {analyticsService} from "./core/analytics";

const AnalyticsObserver = () => {
  const location = useLocation();
  const [user] = application.user.useState();
  const [bussiness] = application.bussiness.useState();
  const [appSettings] = application.appSettings.useState();

  useEffect(() => {
    analyticsService.trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!user) {
      analyticsService.clearUserContext();
      return;
    }
    analyticsService.setUserContext({user, bussiness, appSettings});
  }, [user, bussiness, appSettings]);

  return null;
};

export const App = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      await new Promise((resolve) => {
        application.authReady.addEventListener((ready) => {
          if (ready) return resolve(null);
        });

        if (application.authReady.get()) {
          resolve(true);
          return;
        }

        setTimeout(() => resolve(null), 5000);
      });

      application.initializePrint();
      setLoading(false);
    })();
  }, []);

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      {loading ? (
        <LoadingScreen />
      ) : (
        <CashRegisterProvider>
          <BrowserRouter>
            <AnalyticsObserver />
            <CashRegisterGate />
            <AiCreditsRequiredDialog />
            <AiWelcomeBonusDialog />
            <OnboardingTour />
            <Routes>
              <Route path="/login" Component={LoginPage} />
              <Route path="/entrar" Component={LoginPage} />
              <Route path="/" Component={MarketingHomePage} />
              <Route path="/planos" Component={MarketingPlansPage} />
              <Route path="/assinar" Component={MarketingSubscribePage} />
              <Route path="/checkout" Component={MarketingCheckoutPage} />
              <Route path="/painel" Component={DashboardPage} />
              <Route path="/vender" Component={SellPage} />
              <Route path="/caixa" Component={CashRegisterPage} />
              <Route path="/caixa/relatorio" Component={CashRegisterReportsPage} />
              <Route path="/caixa/sessao/:sessionId" Component={CashRegisterSessionDetailPage} />
              <Route path="/caixa/fechar" Component={CashRegisterClosePage} />
              <Route path="/contas-a-pagar" Component={PayablesListPage} />
              <Route path="/contas-a-pagar/nova" Component={PayableFormPage} />
              <Route path="/contas-a-pagar/editar/:billId" Component={PayableFormPage} />
              <Route path="/clientes" Component={CustomersListPage} />
              <Route path="/clientes/relatorio" Component={CreditReportPage} />
              <Route path="/clientes/:customerId" Component={CustomerDetailPage} />
              <Route path="/vendas/estatisticas" Component={SalesAnalyticsPage} />
              <Route path="/vendas/:sellId" Component={SaleDetailPage} />
              <Route path="/produtos" Component={ProductsPage} />
              <Route path="/produtos/novo" Component={ProductFormPage} />
              <Route path="/produtos/categorias" Component={ProductCategoriesPage} />
              <Route path="/produtos/fornecedores" Component={ProductsupplierPage} />
              <Route path="/produtos/estoque" Component={InventoryPage} />
              <Route path="/produtos/cadastro-em-massa" Component={BulkProductsPage} />
              <Route path="/produtos/editar/:productId" Component={ProductFormPage} />
              <Route
                path="/produtos/qrcode/:qrcodeId/:token"
                Component={ProductQrcodePage}
              />
              <Route path="/usuarios" Component={UsersPage} />
              <Route path="/comissoes" Component={CommissionsPage} />
              <Route path="/meu-plano" Component={MyPlanPage} />
              <Route path="/configuracoes" Component={SettingsPage} />
              <Route path="/suporte/base-de-conhecimento" Component={KnowledgeBasePage} />
              <Route path="/suporte/atendimento" Component={SupportContactPage} />
              <Route path="/403" Component={NotPermissionPage} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </CashRegisterProvider>
      )}
    </ThemeProvider>
  );
};
